import { View } from "extensions/marionette";
import {
    captureException,
    setUserContext,
    captureBreadcrumb
} from "extensions/sentry";
import { DisabledAccountLogin, PasswordExpired } from "common/helpers/errors";
import { showPassword } from "common/helpers/effects";
import { DO_LOGIN } from "common/actions/authentication";
import { CLEAR_AUTH_INFO, STORE_AUTH_INFO } from "common/reducers/authentication";

// There is a bug in Chromium that prevents float label working on autofilled password:
// https://bugs.chromium.org/p/chromium/issues/detail?id=669724
import { FloatLabels } from "extensions/floatLabels";

import strings from "common/strings";
import { store } from "common/store";
import {
    destroyPopovers
} from "common/utils";

import { Dialog } from "common/components/dialog/dialog";

import { request } from "common/components/request";
import { getAPI } from "common/components/utils/getApi";
import { pazienHistory } from "common/components/pazienHistory";

import loaderTemplate from "common/helpers/horizontal-loader.jst";
import { providerAuthStep2Message } from "merchant/features/userManagement/sso_helper";

import loginTemplateFrame from "./templateFrame.jst";
import templateBody from "./login.jst";

export const LoginView = View.extend({
    template: (data) => loginTemplateFrame({
        partial: templateBody,
        api: getAPI(true),
        ...data
    }),
    ui: {
        form: "#merchant-login-form",
        showPassword: ".showPassword",
        "global-error": "#login-status",
        "email-error": ".email-error",
        "password-error": ".pass-error",
        password: "#password",
        loginButton: ".btn-primary",
        email: "#email"
    },
    events: {
        "submit @ui.form": "loginUser",
        "click .sso-redirect-link": "loginUser",
        "click @ui.showPassword": showPassword,
        "keypress @ui.email": "preventSpaces",
        "click .not-me": function(e) {
            e.preventDefault();
            // Reset state
            this.state.step = 1;
            delete this.state.email;
            delete this.state.authInfo;

            // Clean stored user info
            store.dispatch(CLEAR_AUTH_INFO());

            // Re-render the view
            this.render();
    
            this.updateFloatLabels();

            this.ui.email.focus();
            
            // Redirect to login page
            pazienHistory.navigate("login");
        },
        "click .reset-pass": "resetPassword",
        "click .trouble-sign-in": "showNoEmailFAQ"
    },

    initialize({ state = {} } = {}) {
        this.state = {
            step: 1,
            waiting: false,
            ...state
        };
    },
    
    resetPassword(e) {
        const newFragment = pazienHistory.addParamsToFragment("reset", {
            email: this.state.email || this.ui.email.val()
        });
        
        pazienHistory.navigate(newFragment, { trigger: true });
        e.preventDefault();
    },
    
    showNoEmailFAQ(e) {
        Dialog.show({
            title: strings.login.troubleSignIn,
            dismissable: true,
            backdrop: "static",
            content: () => strings.login.cantLogin,
            render: true
        });
        
        e.preventDefault();
    },
    
    preventSpaces(e) {
        return e.which !== 32;
    },
    
    showError(error) {
        this.hideAllOpenAlerts();
    
        // Display error in appropriate box
        const errorCont = this.ui[`${error.type || "global"}-error`];
        if (errorCont.html) {
            errorCont.html(error.message || error).removeClass("hidden");
        }
    },
    
    addSpinnerToLoginButton() {
        this.state.waiting = true;
        this.state.oldLoginBtnText = this.ui.loginButton.text();
        this.ui.loginButton.html(loaderTemplate()).find(".loading-spinner").addClass("white");
    },
    
    removeSpinnerFromLoginButton() {
        if (this.isDestroyed()) return;
        
        this.state.waiting = false;
        this.ui.loginButton.html(this.state.oldLoginBtnText || strings.login.signIn);
    },

    getAuthInfo({ username }) {
        return request("/user/sso", {
            data: { username },
            ensureAuth: false
        }).then((data) => {
            this.state.authInfo = {
                username,
                ...data
            };
            
            // Set user context to Sentry.
            setUserContext({
                username
            });
    
            captureBreadcrumb({
                message: "Fetched user authentication status",
                category: "security",
                data: this.state.authInfo
            });

            // Redirect immediately if sso url is available
            if (data.sso_redirect_url) {
                window.location.href = data.sso_redirect_url;
                return data;
            }

            if (data.sso) {
                // This is sso user. Send him to the SSO sign-in page
                const sso_url = new URL(`${store.getState().config.api_host}/saml/${data.sso}/login`);
                
                if (this.state.email || this.ui.email.val()) {
                    sso_url.searchParams.set("username", this.state.email || this.ui.email.val());
                }
                
                window.location.href = sso_url.toString();
            }
            return data;
        });
    },

    loginUser(event) {
        event.preventDefault();
        destroyPopovers();
        this.hideAllOpenAlerts();
        
        // Return earlier if prev attempt is still pending
        if (this.state.waiting) return;

        this.addSpinnerToLoginButton();

        const isPazienUser = this.isPazienUser();
        const isSsoUser = this.isSsoUser();
        
        if (isPazienUser || (!isPazienUser && !isSsoUser && this.state.step === 2)) {
            const formData = Object.fromEntries(new FormData(this.ui.form[0]));
            store.dispatch(DO_LOGIN({
                email: formData.email || this.state.email || "",
                password: formData.password
            })).then(() => {
                store.dispatch(STORE_AUTH_INFO(this.state.authInfo));
    
                // Record event in Sentry if login took more that 20s
                this.timeoutTimer = setTimeout(() => {
                    const error = new Error("Login is taking longer than 20s");
                    error.extra = this.state;
                    captureException(error);
                    this.timeoutTimer = null;
                }, 20_000);
            }).catch((error) => {
                if (error instanceof PasswordExpired) {
                    this.getOption("onPasswordExpired")(error);
                    return;
                }
                
                if (error instanceof DisabledAccountLogin && error.extra.description === "inactivity_lock") {
                    const newFragment = pazienHistory.addParamsToFragment("reset", {
                        email: this.state.email || this.ui.email.val(),
                        reason: "inactivity_lock"
                    });
                    
                    pazienHistory.navigate(newFragment, { trigger: true });
                    return;
                }
                console.error(error);
                this.showError(error);
    
                // Remove spinner
                this.removeSpinnerFromLoginButton();
            });
        } else if (isSsoUser && this.state.step === 2) {
            // This is sso user. Send him to the SSO sign-in page
            const sso_provider = this.getSsoProvider();
            const sso_url = new URL(`${store.getState().config.api_host}/saml/${sso_provider}/login`);
            
            if (this.state.email || this.ui.email.val()) {
                sso_url.searchParams.set("username", this.state.email || this.ui.email.val());
            }
            
            window.location.href = sso_url.toString();
        } else {
            const email = this.ui.email.val();
            
            captureBreadcrumb({
                message: "Fetching user authentication status",
                category: "security",
                data: {
                    username: email
                }
            });
            
            // Validate email and check the user info
            if (email.trim().length) {
                this.hideAllOpenAlerts();
                this.getAuthInfo({
                    username: email
                }).then(() => {
                    const provider = this.getSsoProvider() || "pazien";
                    pazienHistory.navigate(`login/provider/${provider}`);
                    
                    this.state.step = 2;
                    this.state.email = email;
                    this.removeSpinnerFromLoginButton();
                    this.render();
                    this.updateFloatLabels();
                    this.ui.password.focus();
                }).catch((error) => {
                    this.removeSpinnerFromLoginButton();
                    this.showError(strings.common.errors.internal);
                });
            } else {
                this.showError({
                    type: "email",
                    message: "Enter your email address."
                });
    
                // Remove spinner
                this.removeSpinnerFromLoginButton();
            }
        }
    },

    hideAllOpenAlerts() {
        this.$(".alert-danger").addClass("hidden");
    },
    
    updateFloatLabels() {
        this.floatLabelInstance = new FloatLabels("#merchant-login-form", {
            style: 2
        });
    },
    
    onDestroy() {
        if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
        }
        this.floatLabelInstance = null;
    },
    
    onAttach() {
        this.updateFloatLabels();
        
        if (this.options.showTimeoutMessage) {
            this.showError(strings.timeoutLoginMessage);
        }
        
        this.ui.email.focus();
    },
    
    getStoredUserInfo() {
        if (this.state.authInfo) {
            return this.state.authInfo;
        }
        
        return {};
    },
    
    checkUser(predicate) {
        const user = this.getStoredUserInfo();
        return predicate(user);
    },
    
    isSsoUser() {
        return this.checkUser((user) => typeof user.sso === "string");
    },
    
    isPazienUser() {
        return this.checkUser((user) => user.sso === null);
    },
    
    getSsoProvider() {
        const user = this.getStoredUserInfo();
        return user.sso;
    },
    
    templateContext() {
        const ctx = {
            isPazienUser: this.isPazienUser(),
            isSsoUser: this.isSsoUser(),
            userInfo: this.getStoredUserInfo(),
            signInTitle: strings.login.signInTitle,
            ...this.state
        };
        
        if (ctx.isSsoUser) {
            Object.assign(ctx, providerAuthStep2Message(this.getSsoProvider()));
        }
        
        return {
            ...ctx,
            showNextButton: ctx.step === 1 && ctx.isPazienUser === false,
            // Show email input when we're on step 1 or when user doesn't have a first name and not SSO user
            showEmailInput: ctx.step === 1 || (ctx.step === 2 && !ctx.isSsoUser),
            // Show password input when we know it's a pazien user or when user is unknown and we're on the step 2
            showPasswordInput: ctx.isPazienUser === true || (ctx.isPazienUser === false && ctx.isSsoUser === false && ctx.step === 2),
            showHowToSignInWithSSO: ctx.step === 2 && ctx.isSsoUser
        };
    }
});
